// BHP-1132 / BHP-1147: create a generic VPS through a guided, Hetzner-like flow.
//
// A dialog, like VpsEditModal and RegisterDomainModal, so the keyboard contract
// (focus in, Tab cycles, Escape closes, focus back out) comes from useDialogA11y.
//
// BHP-1147 replaced the flat dropdown with a steered choice: pick a LOCATION
// first, then a CATEGORY, then an ARCHITECTURE, then the TYPE. Location drives
// the one availability call (BHP-1146, `?location=`); category (BHP-1151) and
// architecture (BHP-1154) are client-side filters, so narrowing them makes no
// new request. Out-of-stock types stay visible but are not choosable — never
// silently hidden.
//
// Form state is kept inline (ADR-0001: component-local orchestration lives in
// the component; only pure rules go to domain/). The pure filters/sorts live in
// domain/vpsCreate.ts (`typesForCategoryAndArchitecture`, `architecturesInCategory`).
//
// Creating a server costs money from the moment it exists, so the actual POST
// sits behind a confirmation that names the price — the same line the power
// actions take (BHP-1123). The active customer travels in the body as
// customerUuid (BHP-1130 requires it) as well as in the x-active-customer header.
//
// No labels field: `POST /api/vps` does not accept custom labels (BHP-1130); a
// customer who wants labels adds them after creation through VpsEditModal.
import { useState } from 'react';
import { toFormErrors } from '../../../shared/http/errorMessage';
import { useDialogA11y } from '../../../shared/ui/useDialogA11y';
import { useActiveCustomerStore } from '../../customer/application/activeCustomerStore';
import {
  type Architecture,
  ARCHITECTURES,
  architecturesInCategory,
  CATEGORIES,
  type Category,
  formatMonthlyPrice,
  hostnameError,
  LOCATIONS,
  typesForCategoryAndArchitecture,
  type VpsCreateInput
} from '../domain/vpsCreate';
import {
  useHetznerProjects,
  useServerTypes
} from '../application/vpsCreateQueries';
import { useVpsCreate } from '../application/vpsCreateMutation';
import InlineConfirm from '../../../shared/ui/InlineConfirm';

// Same field vocabulary as VpsEditModal / DnsRecordForm.
const FIELD =
  'w-full rounded-md border border-solid border-slate-300 px-3 py-1.5 text-sm text-slate-800 focus:border-teal-500 focus:outline-none';
const LABEL = 'mb-1 block text-xs font-medium text-slate-600';

/** Fields the form renders, so a violation can be bound to its input. */
const FORM_FIELDS = ['hostname'] as const;

/** One selectable card (location, category, architecture or type), styled by its state. */
function cardClass(selected: boolean, disabled = false): string {
  const base =
    'rounded-lg border border-solid px-3 py-2 text-left text-sm transition';
  if (disabled) {
    return `${base} cursor-not-allowed border-slate-200 bg-slate-50 text-slate-400`;
  }
  if (selected) {
    return `${base} border-teal-500 bg-teal-50 text-slate-900 ring-1 ring-teal-500`;
  }
  return `${base} border-slate-300 bg-white text-slate-700 hover:border-teal-400 hover:bg-slate-50`;
}

export default function VpsCreateModal({
  onClose
}: {
  onClose: () => void;
}): JSX.Element {
  const create = useVpsCreate();
  const projectsQuery = useHetznerProjects();
  const activeCustomerUuid = useActiveCustomerStore(
    (state) => state.activeCustomerUuid
  );
  const dialogRef = useDialogA11y<HTMLDivElement>(onClose);

  const [hostname, setHostname] = useState('');
  const [location, setLocation] = useState('');
  const [category, setCategory] = useState<Category | ''>('');
  const [architecture, setArchitecture] = useState<Architecture | ''>('');
  const [selectedIdentifier, setSelectedIdentifier] = useState('');
  const [selectedProjectUuid, setSelectedProjectUuid] = useState('');
  const [stage, setStage] = useState<'form' | 'confirm'>('form');
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [formError, setFormError] = useState<string | null>(null);

  // Availability is asked for the chosen location only; the query stays disabled
  // (and thus not pending) until one is picked.
  const serverTypesQuery = useServerTypes(location);
  const serverTypes = serverTypesQuery.data?.items ?? [];
  const projects = projectsQuery.data?.items ?? [];

  const typesLoading = location !== '' && serverTypesQuery.isPending;
  const typesError = location !== '' && serverTypesQuery.isError;
  const hasNoProject =
    !projectsQuery.isPending && !projectsQuery.isError && projects.length === 0;

  // Category then architecture narrow the already-fetched list — no second
  // request. A category with a single architecture (regular/general purpose are
  // x86-only) needs no choice: that architecture is applied implicitly and its
  // step is skipped, so the customer only sees it when it actually matters
  // (cost_optimized, which mixes x86 and ARM).
  const presentArchitectures = architecturesInCategory(serverTypes, category);
  const soleArchitecture: Architecture | '' =
    presentArchitectures.size === 1 ? [...presentArchitectures][0] : '';
  const effectiveArchitecture =
    soleArchitecture !== '' ? soleArchitecture : architecture;
  const visibleTypes = typesForCategoryAndArchitecture(
    serverTypes,
    category,
    effectiveArchitecture
  );
  const selectedType = serverTypes.find(
    (type) => type.identifier === selectedIdentifier
  );

  // One project: nothing to choose, so it is used implicitly. Several: the
  // customer picks. The picked value drives both the body and the confirmation.
  const projectUuid =
    projects.length === 1 ? projects[0].uuid : selectedProjectUuid;

  const chooseLocation = (code: string): void => {
    setLocation(code);
    // The type belongs to the previous location; the category and architecture
    // axes are fixed taxonomies, so they stay valid across a location change.
    setSelectedIdentifier('');
  };

  const chooseCategory = (value: Category): void => {
    setCategory(value);
    // Architecture belongs to the previous category; the type to the previous
    // architecture. Both are re-chosen below.
    setArchitecture('');
    setSelectedIdentifier('');
  };

  const chooseArchitecture = (value: Architecture): void => {
    setArchitecture(value);
    setSelectedIdentifier('');
  };

  const proceed = (): void => {
    setFieldErrors({});
    setFormError(null);

    const hostError = hostnameError(hostname);
    if (hostError !== null) {
      setFieldErrors({ hostname: hostError });
      return;
    }
    if (location === '') {
      setFormError('Choose a location.');
      return;
    }
    if (selectedType === undefined) {
      setFormError('Choose a server type.');
      return;
    }
    if (projectUuid === '') {
      setFormError('Choose a Hetzner project.');
      return;
    }
    if (activeCustomerUuid === null) {
      setFormError('Select an active customer first.');
      return;
    }

    setStage('confirm');
  };

  const confirmCreate = (): void => {
    if (selectedType === undefined || activeCustomerUuid === null) {
      return;
    }

    const input: VpsCreateInput = {
      hostname,
      hetznerAccountUuid: projectUuid,
      serverType: selectedType.serverType,
      location: selectedType.location,
      customerUuid: activeCustomerUuid
    };

    create.save(input).then(onClose, (error: unknown) => {
      // Back to the form so the customer can fix what the backend rejected,
      // instead of being stuck on a confirmation for a request that failed.
      const { fields, message } = toFormErrors(
        error,
        'The server could not be created; nothing was started.',
        FORM_FIELDS
      );
      setFieldErrors(fields);
      setFormError(message);
      setStage('form');
    });
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="vps-create-title"
        className="w-full max-w-2xl overflow-hidden rounded-xl border border-solid border-slate-200 bg-white shadow-xl"
      >
        <div className="border-0 bg-slate-50 px-5 py-3">
          <h3
            id="vps-create-title"
            className="text-base font-semibold text-slate-900"
          >
            Create VPS
          </h3>
        </div>

        <form
          onSubmit={(event) => {
            event.preventDefault();
            proceed();
          }}
        >
          <div className="max-h-[70vh] space-y-4 overflow-y-auto px-5 py-4">
            {hasNoProject ? (
              <p className="rounded-md border border-solid border-amber-200 bg-amber-50 p-2 text-sm text-amber-900">
                This customer has no Hetzner project yet, so a VPS cannot be
                created. Contact us to set one up.
              </p>
            ) : null}

            <div>
              <label className={LABEL} htmlFor="vps-create-hostname">
                Hostname
              </label>
              <input
                id="vps-create-hostname"
                value={hostname}
                onChange={(event) => {
                  setHostname(event.target.value);
                }}
                className={FIELD}
              />
              {fieldErrors.hostname === undefined ? null : (
                <p className="mt-1 text-xs text-rose-600">
                  {fieldErrors.hostname}
                </p>
              )}
            </div>

            <fieldset className="border-0 p-0">
              <legend className={LABEL}>Location</legend>
              <div className="flex flex-wrap gap-2">
                {LOCATIONS.map((loc) => (
                  <button
                    key={loc.code}
                    type="button"
                    aria-pressed={location === loc.code}
                    onClick={() => {
                      chooseLocation(loc.code);
                    }}
                    className={cardClass(location === loc.code)}
                  >
                    {loc.label}
                  </button>
                ))}
              </div>
            </fieldset>

            {location === '' ? null : typesError ? (
              <p
                role="alert"
                className="rounded-md border border-solid border-rose-200 bg-rose-50 p-2 text-sm text-rose-700"
              >
                Could not load the server types for this location. Please try
                again.
              </p>
            ) : (
              <>
                <fieldset className="border-0 p-0">
                  <legend className={LABEL}>Category</legend>
                  <div className="flex flex-wrap gap-2">
                    {CATEGORIES.map((cat) => (
                      <button
                        key={cat.value}
                        type="button"
                        aria-pressed={category === cat.value}
                        onClick={() => {
                          chooseCategory(cat.value);
                        }}
                        className={cardClass(category === cat.value)}
                      >
                        {cat.label}
                      </button>
                    ))}
                  </div>
                </fieldset>

                {category === '' || !typesLoading ? null : (
                  <p className="text-sm text-slate-500">Loading…</p>
                )}

                {/* Only offer the architecture choice when the category actually
                    mixes x86 and ARM; a single-architecture category applies it
                    implicitly (see effectiveArchitecture). */}
                {presentArchitectures.size > 1 ? (
                  <fieldset className="border-0 p-0">
                    <legend className={LABEL}>Architecture</legend>
                    <div className="flex gap-2">
                      {ARCHITECTURES.map((arch) => (
                        <button
                          key={arch.value}
                          type="button"
                          aria-pressed={architecture === arch.value}
                          onClick={() => {
                            chooseArchitecture(arch.value);
                          }}
                          className={cardClass(architecture === arch.value)}
                        >
                          {arch.label}
                        </button>
                      ))}
                    </div>
                  </fieldset>
                ) : null}

                {effectiveArchitecture === '' ? null : (
                  <fieldset className="border-0 p-0">
                    <legend className={LABEL}>Server type</legend>
                    {visibleTypes.length === 0 ? (
                      <p className="text-sm text-slate-500">
                        No types are available for this architecture in this
                        location.
                      </p>
                    ) : (
                      <div className="grid auto-rows-fr grid-cols-2 gap-2 sm:grid-cols-3">
                        {visibleTypes.map((type) => (
                          <button
                            key={type.identifier}
                            type="button"
                            disabled={!type.available}
                            aria-pressed={
                              selectedIdentifier === type.identifier
                            }
                            aria-label={
                              type.available
                                ? type.serverType
                                : `${type.serverType} (out of stock)`
                            }
                            onClick={() => {
                              setSelectedIdentifier(type.identifier);
                            }}
                            className={`${cardClass(
                              selectedIdentifier === type.identifier,
                              !type.available
                            )} flex h-full flex-col`}
                          >
                            <span className="block font-semibold uppercase">
                              {type.serverType}
                            </span>
                            <span className="mt-0.5 line-clamp-2 block text-xs text-slate-500">
                              {type.description}
                            </span>
                            <span className="mt-auto pt-1 block text-xs font-medium">
                              {type.available
                                ? `${formatMonthlyPrice(type.monthlyPrice)}/month`
                                : 'Out of stock'}
                            </span>
                          </button>
                        ))}
                      </div>
                    )}
                  </fieldset>
                )}
              </>
            )}

            {projects.length > 1 ? (
              <div>
                <label className={LABEL} htmlFor="vps-create-project">
                  Hetzner project
                </label>
                <select
                  id="vps-create-project"
                  value={selectedProjectUuid}
                  onChange={(event) => {
                    setSelectedProjectUuid(event.target.value);
                  }}
                  className={FIELD}
                >
                  <option value="">Choose a project</option>
                  {projects.map((project) => (
                    <option key={project.uuid} value={project.uuid}>
                      {project.projectName}
                    </option>
                  ))}
                </select>
              </div>
            ) : projects.length === 1 ? (
              <div>
                <p className={LABEL}>Hetzner project</p>
                <p className="m-0 text-sm text-slate-700">
                  {projects[0].projectName}
                </p>
              </div>
            ) : null}

            {stage === 'confirm' && selectedType !== undefined ? (
              <div className="rounded-lg border border-solid border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
                <InlineConfirm
                  tone="warning"
                  prompt={
                    <p className="m-0">
                      Create <strong>{hostname}</strong> (
                      <span className="uppercase">
                        {selectedType.serverType}
                      </span>
                      ) for{' '}
                      <strong>
                        {formatMonthlyPrice(selectedType.monthlyPrice)}/month
                      </strong>
                      ? This starts a server that is billed monthly.
                    </p>
                  }
                  confirmLabel="Yes, create"
                  pendingLabel="Creating…"
                  cancelLabel="Back"
                  isPending={create.isPending}
                  onConfirm={confirmCreate}
                  onCancel={() => {
                    setStage('form');
                  }}
                />
              </div>
            ) : null}

            {formError === null ? null : (
              <p
                role="alert"
                className="rounded-md border border-solid border-rose-200 bg-rose-50 p-2 text-sm text-rose-700"
              >
                {formError}
              </p>
            )}
          </div>

          {stage === 'form' ? (
            <div className="flex justify-end gap-2 border-0 bg-slate-50 px-5 py-3">
              <button
                type="button"
                onClick={onClose}
                className="rounded-md border border-solid border-slate-300 bg-white px-3 py-1.5 text-sm font-medium text-slate-700 hover:bg-slate-50"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={projectsQuery.isPending || hasNoProject}
                className="rounded-md border-0 bg-teal-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-teal-700 disabled:opacity-50"
              >
                Continue
              </button>
            </div>
          ) : null}
        </form>
      </div>
    </div>
  );
}
